// Little game sounds made with Web Audio, so there are no audio files to download or cache.

let ctx: AudioContext | null = null;
const MUTE_KEY = 'cm:mg:muted';

export function isMuted(): boolean {
  try {
    return localStorage.getItem(MUTE_KEY) === '1';
  } catch {
    return false;
  }
}

export function setMuted(muted: boolean): void {
  try {
    localStorage.setItem(MUTE_KEY, muted ? '1' : '0');
  } catch {
    // Not remembered.
  }
}

function audio(): AudioContext | null {
  if (isMuted()) return null;
  try {
    const Ctor = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctor) return null;
    ctx ??= new Ctor();
    if (ctx.state === 'suspended') void ctx.resume();
    return ctx;
  } catch {
    return null;
  }
}

function tone(freq: number, start: number, duration: number, type: OscillatorType, volume: number, endFreq?: number) {
  const a = audio();
  if (!a) return;
  const t = a.currentTime + start;
  const osc = a.createOscillator();
  const gain = a.createGain();
  osc.type = type;
  osc.frequency.setValueAtTime(freq, t);
  if (endFreq) osc.frequency.exponentialRampToValueAtTime(endFreq, t + duration);
  gain.gain.setValueAtTime(0.0001, t);
  gain.gain.exponentialRampToValueAtTime(volume, t + 0.02);
  gain.gain.exponentialRampToValueAtTime(0.0001, t + duration);
  osc.connect(gain).connect(a.destination);
  osc.start(t);
  osc.stop(t + duration + 0.05);
}

/** The "arre!" buzzer: someone said a forbidden word, or time is up. */
export function buzzer(): void {
  tone(150, 0, 0.55, 'sawtooth', 0.25);
  tone(155, 0, 0.55, 'square', 0.12);
  if ('vibrate' in navigator) navigator.vibrate?.(250);
}

/** A bright two-note ding for a correct answer. */
export function ding(): void {
  tone(880, 0, 0.14, 'sine', 0.25);
  tone(1320, 0.1, 0.22, 'sine', 0.22);
}

/** A soft tick for the last few seconds. */
export function tick(): void {
  tone(1000, 0, 0.05, 'square', 0.06);
}

/** A quick swoosh for skips. */
export function swoosh(): void {
  tone(600, 0, 0.18, 'triangle', 0.12, 250);
}

/** Unlock audio on the first tap (mobile browsers start muted until the user interacts). */
export function primeAudio(): void {
  audio();
}
